import { TEXT_SIZE_ATTRIBUTE, TEXT_SIZE_STORAGE_KEY, asTextSize, textSizeAttributeValue, type TextSize } from '@bms2/shared';

/**
 * The person's own text size on the page (piece 12; decision 1321). 🔒 The
 * format is one attribute on the document root, and the token file is the only
 * reader of it: every size a component draws comes from a token, so Large
 * reaches every screen without any component knowing which format it is in.
 *
 * 🔨 The stored row is the record and the frame's answer is what applies it.
 * The browser keeps a copy only so a Large reader's first paint is already
 * Large; a store that is absent, blocked or throws reads as Normal.
 */
function safeStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    // silent: a browser refusing storage forgets the choice, nothing more.
    return null;
  }
}

/** Draws the page in a format at once and remembers it on this browser. */
export function applyTextSize(size: TextSize, root: HTMLElement | null = typeof document === 'undefined' ? null : document.documentElement, store: Pick<Storage, 'setItem'> | null = safeStore()): void {
  const value = textSizeAttributeValue(size);
  if (root) {
    if (value === null) root.removeAttribute(TEXT_SIZE_ATTRIBUTE);
    else root.setAttribute(TEXT_SIZE_ATTRIBUTE, value);
  }
  try {
    store?.setItem(TEXT_SIZE_STORAGE_KEY, size);
  } catch {
    // silent: a browser refusing storage forgets the choice, nothing more
    // 🔨 A browser refusing to store still draws the format now; only the first paint of the next load is at Normal.
  }
}

/** The format this browser last drew, Normal where it remembers none. */
export function rememberedTextSize(store: Pick<Storage, 'getItem'> | null = safeStore()): TextSize {
  try {
    return asTextSize(store?.getItem(TEXT_SIZE_STORAGE_KEY));
  } catch {
    // silent: a browser refusing storage forgets the choice, nothing more.
    return 'Normal';
  }
}
